import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { 
  ShieldBan, 
  Search, 
  Plus, 
  ShieldOff,
  Clock,
  Ban,
  AlertTriangle,
  RefreshCw,
  ArrowLeft
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import KPICard from "@/components/admin/KPICard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface BlockedIp {
  id: string;
  ip_address: string;
  reason: string;
  blocked_by: string | null;
  blocked_at: string;
  expires_at: string | null;
  is_active: boolean;
  violation_count: number;
  last_violation_at: string | null;
  user_agent: string | null;
  related_user_id: string | null;
}

interface BlockedIpsResponse {
  blockedIps: BlockedIp[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  stats: {
    total: number;
    active: number;
    expired: number;
    permanent: number;
    autoBlocked: number;
  };
  recentViolations: {
    ip_address: string;
    violation_count: number;
    last_violation_at: string;
    endpoint: string;
  }[];
}

export default function BlockedIPs() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<BlockedIp | null>(null);

  // Form state for manual block
  const [newIp, setNewIp] = useState("");
  const [newReason, setNewReason] = useState("");
  const [newDuration, setNewDuration] = useState<string>("24");

  // Fetch blocked IPs
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["blocked-ips", page, search, statusFilter],
    queryFn: async (): Promise<BlockedIpsResponse> => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("get-blocked-ips", {
        body: { page, pageSize: 20, status: statusFilter, search },
      });

      if (response.error) throw response.error;
      return response.data;
    },
  });

  // Block IP mutation
  const blockMutation = useMutation({
    mutationFn: async (params: { ipAddress: string; reason: string; durationHours: number | null }) => {
      const response = await supabase.functions.invoke("manage-blocked-ip", {
        body: { 
          action: "block", 
          ipAddress: params.ipAddress, 
          reason: params.reason,
          durationHours: params.durationHours,
        },
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast({ title: t("admin.blockedIps.messages.blockSuccess") });
      queryClient.invalidateQueries({ queryKey: ["blocked-ips"] });
      setShowBlockModal(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({ 
        title: t("common.error"), 
        description: error.message,
        variant: "error" 
      });
    },
  });

  // Unblock IP mutation
  const unblockMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await supabase.functions.invoke("manage-blocked-ip", {
        body: { action: "unblock", id },
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast({ title: t("admin.blockedIps.messages.unblockSuccess") });
      queryClient.invalidateQueries({ queryKey: ["blocked-ips"] });
      setShowUnblockDialog(false);
      setSelectedBlock(null);
    },
    onError: (error: any) => {
      toast({ 
        title: t("common.error"), 
        description: error.message,
        variant: "error" 
      });
    },
  });

  const resetForm = () => {
    setNewIp("");
    setNewReason("");
    setNewDuration("24");
  };

  const handleBlock = () => {
    if (!newIp || !newReason) {
      toast({ title: t("common.error"), description: "IP and reason required", variant: "error" });
      return;
    }
    const durationHours = newDuration === "permanent" ? null : parseInt(newDuration);
    blockMutation.mutate({ ipAddress: newIp, reason: newReason, durationHours });
  };

  const handleUnblock = () => {
    if (selectedBlock) {
      unblockMutation.mutate(selectedBlock.id);
    }
  };

  const stats = data?.stats || { total: 0, active: 0, expired: 0, permanent: 0, autoBlocked: 0 };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-2">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin">Admin</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.blockedIps.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.goBack")}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold uppercase flex items-center gap-3">
                <ShieldBan className="w-8 h-8" />
                {t("admin.blockedIps.title")}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t("admin.blockedIps.subtitle")}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <BrutalButton variant="secondary" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("common.refresh", "Refresh")}
            </BrutalButton>
            <BrutalButton onClick={() => setShowBlockModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t("admin.blockedIps.blockIp")}
            </BrutalButton>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            title={t("admin.blockedIps.kpi.total")}
            value={stats.total}
          />
          <KPICard
            title={t("admin.blockedIps.kpi.active")}
            value={stats.active}
          />
          <KPICard
            title={t("admin.blockedIps.kpi.permanent")}
            value={stats.permanent}
          />
          <KPICard
            title={t("admin.blockedIps.kpi.expired")}
            value={stats.expired}
          />
        </div>

        {/* Filters */}
        <BrutalCard className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t("admin.blockedIps.searchPlaceholder", "Search by IP or reason...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border-2 border-dark"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] border-2 border-dark">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("admin.blockedIps.filters.all", "All")}</SelectItem>
                <SelectItem value="active">{t("admin.blockedIps.filters.active", "Active")}</SelectItem>
                <SelectItem value="permanent">{t("admin.blockedIps.filters.permanent", "Permanent")}</SelectItem>
                <SelectItem value="temporary">{t("admin.blockedIps.filters.temporary", "Temporary")}</SelectItem>
                <SelectItem value="expired">{t("admin.blockedIps.filters.expired", "Expired")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </BrutalCard>

        {/* Table */}
        <BrutalCard className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.blockedIps.table.ipAddress")}</TableHead>
                <TableHead>{t("admin.blockedIps.table.reason")}</TableHead>
                <TableHead>{t("admin.blockedIps.table.blockedAt")}</TableHead>
                <TableHead>{t("admin.blockedIps.table.expiresAt")}</TableHead>
                <TableHead>{t("admin.blockedIps.table.violations")}</TableHead>
                <TableHead>{t("admin.blockedIps.table.status")}</TableHead>
                <TableHead>{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    {t("common.loading")}
                  </TableCell>
                </TableRow>
              ) : data?.blockedIps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {t("admin.blockedIps.noResults", "No blocked IPs found")}
                  </TableCell>
                </TableRow>
              ) : (
                data?.blockedIps.map((block) => (
                  <TableRow key={block.id}>
                    <TableCell className="font-mono text-sm">
                      {block.ip_address}
                    </TableCell>
                    <TableCell className="max-w-xs truncate" title={block.reason}>
                      {block.reason}
                    </TableCell>
                    <TableCell>
                      {format(new Date(block.blocked_at), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      {block.expires_at 
                        ? format(new Date(block.expires_at), "dd/MM/yyyy HH:mm")
                        : <span className="text-destructive font-medium">{t("admin.blockedIps.permanent")}</span>
                      }
                    </TableCell>
                    <TableCell>
                      <BrutalBadge variant="secondary">
                        {block.violation_count}
                      </BrutalBadge>
                    </TableCell>
                    <TableCell>
                      {block.is_active ? (
                        <BrutalBadge variant="error">
                          {t("admin.blockedIps.active")}
                        </BrutalBadge>
                      ) : (
                        <BrutalBadge variant="secondary">
                          {t("admin.blockedIps.expired")}
                        </BrutalBadge>
                      )}
                    </TableCell>
                    <TableCell>
                      {block.is_active && (
                        <BrutalButton
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedBlock(block);
                            setShowUnblockDialog(true);
                          }}
                        >
                          <ShieldOff className="w-4 h-4" />
                        </BrutalButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex justify-center gap-2 p-4 border-t border-dark">
              <BrutalButton
                variant="secondary"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                {t("common.previous")}
              </BrutalButton>
              <span className="px-4 py-2 font-mono">
                {page} / {data.totalPages}
              </span>
              <BrutalButton
                variant="secondary"
                size="sm"
                disabled={page === data.totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                {t("common.next")}
              </BrutalButton>
            </div>
          )}
        </BrutalCard>

        {/* Recent Violations */}
        {data?.recentViolations && data.recentViolations.length > 0 && (
          <BrutalCard className="p-4">
            <h3 className="font-heading font-bold uppercase mb-4">
              {t("admin.blockedIps.recentViolations", "Recent Violations")}
            </h3>
            <div className="space-y-2">
              {data.recentViolations.map((v, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-surface rounded border border-dark/10">
                    <span className="font-mono text-sm">{v.ip_address}</span>
                    <span className="text-sm text-muted-foreground">{v.endpoint}</span>
                    <BrutalBadge variant={v.violation_count >= 3 ? "error" : "secondary"}>
                      {v.violation_count} violations
                    </BrutalBadge>
                  </div>
              ))}
            </div>
          </BrutalCard>
        )}
      </div>

      {/* Block IP Modal */}
      <Dialog open={showBlockModal} onOpenChange={setShowBlockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {t("admin.blockedIps.blockIp")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("admin.blockedIps.form.ipAddress", "IP Address")}
              </label>
              <BrutalInput
                placeholder="192.168.1.1"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("admin.blockedIps.form.reason", "Reason")}
              </label>
              <BrutalInput
                placeholder={t("admin.blockedIps.form.reasonPlaceholder", "Reason for blocking...")}
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("admin.blockedIps.form.duration", "Duration")}
              </label>
              <Select value={newDuration} onValueChange={setNewDuration}>
                <SelectTrigger className="border-2 border-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t("admin.blockedIps.duration.1hour", "1 hour")}</SelectItem>
                  <SelectItem value="24">{t("admin.blockedIps.duration.24hours", "24 hours")}</SelectItem>
                  <SelectItem value="168">{t("admin.blockedIps.duration.7days", "7 days")}</SelectItem>
                  <SelectItem value="720">{t("admin.blockedIps.duration.30days", "30 days")}</SelectItem>
                  <SelectItem value="permanent">{t("admin.blockedIps.permanent")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <BrutalButton variant="secondary" onClick={() => setShowBlockModal(false)}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton 
              onClick={handleBlock}
              disabled={blockMutation.isPending}
            >
              {blockMutation.isPending ? t("common.loading") : t("admin.blockedIps.blockIp")}
            </BrutalButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unblock Confirmation */}
      <AlertDialog open={showUnblockDialog} onOpenChange={setShowUnblockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.blockedIps.confirmUnblock")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.blockedIps.confirmUnblockDesc", "Are you sure you want to unblock this IP address?")}
              <br />
              <span className="font-mono mt-2 block">{selectedBlock?.ip_address}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnblock}>
              {t("admin.blockedIps.unblock")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
